import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import axios from '../utils/axios';
import { ENDPOINTS } from '../config/api';

const useAuthStore = create(
  persist(
    (set) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      loading: false,
      error: null,

      // Login
      login: async (credentials) => {
        set({ loading: true, error: null });
        try {
          const response = await axios.post(ENDPOINTS.AUTH.LOGIN, credentials);
          const { data } = response;
          
          // 2FA kontrolü
          if (data.twoFactorRequired) {
            set({ loading: false });
            return data; // twoFactorRequired ve userId döndür
          }
          
          // Normal giriş - token varsa
          if (data.data && data.data.token) {
            const { token, ...userData } = data.data;
            
            localStorage.setItem('token', token);
            
            set({
              user: userData,
              token,
              isAuthenticated: true,
              loading: false,
            });
            
            // Sync cart & wishlist
            try {
              const useCartStore = (await import('./useCartStore')).default;
              const useWishlistStore = (await import('./useWishlistStore')).default;
              await useCartStore.getState().syncCart();
              await useWishlistStore.getState().syncWishlist();
            } catch (err) {
              console.error('Post-login sync failed:', err);
            }
            
            return response;
          }
          
          // Beklenmeyen response
          throw new Error('Geçersiz response formatı');
        } catch (error) {
          set({ error: error.message, loading: false });
          throw error;
        }
      },

      // Login success after 2FA (separate function)
      loginSuccess: async (userData) => {
        const { token, ...user } = userData;
        localStorage.setItem('token', token);
        set({
          user,
          token,
          isAuthenticated: true,
          loading: false,
        });

        // Sync cart & wishlist
        try {
          const useCartStore = (await import('./useCartStore')).default;
          const useWishlistStore = (await import('./useWishlistStore')).default;
          await useCartStore.getState().syncCart();
          await useWishlistStore.getState().syncWishlist();
        } catch (err) {
          console.error('Post-login sync failed:', err);
        }
      },

      // Register
      register: async (userData) => {
        set({ loading: true, error: null });
        try {
          const response = await axios.post(ENDPOINTS.AUTH.REGISTER, userData);
          const { data } = response;
          const { token, ...user } = data.data || {};
          
          if (token) {
            localStorage.setItem('token', token);
          }
          
          set({
            user,
            token,
            isAuthenticated: true,
          });
          
          // Sync cart & wishlist
          try {
            const useCartStore = (await import('./useCartStore')).default;
            const useWishlistStore = (await import('./useWishlistStore')).default;
            await useCartStore.getState().syncCart();
            await useWishlistStore.getState().syncWishlist();
          } catch (err) {
            console.error('Post-login sync failed:', err);
          }
          
          return response;
        } catch (error) {
          set({ error: error.message || 'Kayıt başarısız!' });
          throw error;
        } finally {
          set({ loading: false });
        }
      },

      // Logout
      logout: async () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('guest_cart');
        localStorage.removeItem('guest_wishlist');
        set({
          user: null,
          token: null,
          isAuthenticated: false,
        });
        
        try {
          const useCartStore = (await import('./useCartStore')).default;
          const useWishlistStore = (await import('./useWishlistStore')).default;
          useCartStore.getState().clearCartStore();
          useWishlistStore.getState().clearWishlistStore();
        } catch (err) {
          console.error('Clear stores on logout failed:', err);
        }
      },

      // Get current user
      getCurrentUser: async () => {
        try {
          const response = await axios.get(ENDPOINTS.AUTH.ME);
          const { data } = response;
          set({ user: data.data, isAuthenticated: true });
          return response;
        } catch (error) {
          set({ user: null, token: null, isAuthenticated: false });
          throw error;
        }
      },

      // Update password
      updatePassword: async (passwords) => {
        set({ loading: true, error: null });
        try {
          const response = await axios.put(ENDPOINTS.AUTH.UPDATE_PASSWORD, passwords);
          set({ loading: false });
          return response;
        } catch (error) {
          set({ error: error.message, loading: false });
          throw error;
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);

export default useAuthStore;

